import { FC, memo, useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { messageOnError } from '@/common/utils';

import { DomainVisitors } from '@/modules/Domains';
import {
  DEFAULT_REDIRECT_MATCH_MODE,
  DEFAULT_REDIRECT_STATUS_CODE,
  ParsedRedirectRow,
  RedirectMatchMode,
  RedirectsQueryKeys,
  useBulkCreateRedirectsByPathsMutation,
} from '@/modules/Sites';

import { buildBulkRedirectItems, parseBulkRedirects } from './parseBulkRedirects';
import { RedirectsBulkCreateView } from './RedirectsBulkCreateView';

interface Props {
  siteId: string;
}

export const RedirectsBulkCreateContainer: FC<Props> = memo(({ siteId }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const [text, setText] = useState('');
  const [statusCode, setStatusCode] = useState<number>(DEFAULT_REDIRECT_STATUS_CODE);
  const [matchMode, setMatchMode] = useState<RedirectMatchMode>(DEFAULT_REDIRECT_MATCH_MODE);
  const [forVisitor, setForVisitor] = useState<DomainVisitors>(DomainVisitors.Any);
  const [isActive, setIsActive] = useState(true);

  const deferredText = useDeferredValue(text);
  const isParsePending = deferredText !== text;

  const rows = useMemo<ParsedRedirectRow[]>(() => parseBulkRedirects(deferredText), [deferredText]);

  const validCount = useMemo(
    () => rows.filter(({ pathFrom, pathTo }) => Boolean(pathFrom) && Boolean(pathTo)).length,
    [rows]
  );
  const errorCount = rows.length - validCount;

  const { mutate: bulkCreate, isLoading } = useBulkCreateRedirectsByPathsMutation(siteId)({
    onSuccess: async ({ createdCount, skippedCount }) => {
      await Promise.all([
        queryClient.refetchQueries([RedirectsQueryKeys.GetAll, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.GetAllList, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.Conflicts, siteId]),
      ]);

      appMessageApi.success(
        `Створено редіректів: ${createdCount}` + (skippedCount ? `, пропущено дублів: ${skippedCount}` : '')
      );

      setText('');
    },
    onError: (err) => messageOnError(err, appMessageApi),
  });

  const handleSubmit = useCallback(() => {
    const redirects = buildBulkRedirectItems(rows, statusCode, matchMode, forVisitor, isActive);

    if (!redirects.length) return;

    bulkCreate({ redirects });
  }, [rows, statusCode, matchMode, forVisitor, isActive, bulkCreate]);

  const handleClear = useCallback(() => setText(''), []);

  return (
    <RedirectsBulkCreateView
      text={ text }
      onChangeText={ setText }
      statusCode={ statusCode }
      onChangeStatusCode={ setStatusCode }
      matchMode={ matchMode }
      onChangeMatchMode={ setMatchMode }
      forVisitor={ forVisitor }
      onChangeForVisitor={ setForVisitor }
      isActive={ isActive }
      onChangeIsActive={ setIsActive }
      rows={ rows }
      validCount={ validCount }
      errorCount={ errorCount }
      isLoading={ isLoading }
      isParsePending={ isParsePending }
      onSubmit={ handleSubmit }
      onClear={ handleClear }
    />
  );
});
